/* MolCellQuest — 各時代の風景
   ──────────────────────────────────────────────────────────
   すべて手描きの SVG（viewBox 320×180）。外部の地図データも
   CDN も使わない。
     svg    : 背景の絵。クラス名を付けるだけで CSS アニメが乗る
              （.jsun .jcloud .jcloud2 .jbubble .jbubble2 .jflicker）
     route  : 旅人が進む道の path。d 属性そのもの。
              チェックポイントは data/eras.js の pos（0〜1）で
              この道の上に自動配置される。
     trail  : 通った跡の描き方 "bubble" | "foot" | "dust" | "none"
     stars  : 手続き的に散らす星の数（0 で描かない）
     skyH   : 星を散らす下端の y。地面や海の上に星が出ないよう、
              その時代の「空」の高さを指定する（既定は vbH の 55%）
   ────────────────────────────────────────────────────────── */

const VB_W = 320;
const VB_H = 180;

/**
 * 初期陸上植物（クックソニアなど）。二又に分かれた茎の先に胞子嚢。
 * 「茎＋斜めの2本」を素直に描くと矢印に見えてしまうので、
 * 枝は茎の途中から外へ広がらせ、先端に丸をつけて植物として読ませる。
 */
function forkPlant(x, baseY, h, color = "#8fce74", tip = "#c8f0a0") {
  const top = baseY - h;
  const mid = baseY - h * 0.55;
  const spread = h * 0.34;
  return (
    `<g stroke="${color}" stroke-width="1.7" fill="none" stroke-linecap="round">` +
    `<path d="M${x} ${baseY} L${x} ${mid}"/>` +
    `<path d="M${x} ${mid} Q${x - spread * 0.7} ${(mid + top) / 2} ${x - spread} ${top}"/>` +
    `<path d="M${x} ${mid} Q${x + spread * 0.7} ${(mid + top) / 2} ${x + spread} ${top}"/>` +
    `</g>` +
    `<circle cx="${(x - spread).toFixed(1)}" cy="${top.toFixed(1)}" r="1.8" fill="${tip}"/>` +
    `<circle cx="${(x + spread).toFixed(1)}" cy="${top.toFixed(1)}" r="1.8" fill="${tip}"/>`
  );
}

/** 空と地面の共通下地 */
function ground(topColor, botColor, y = 132) {
  return (
    `<rect x="0" y="${y}" width="${VB_W}" height="${VB_H - y}" fill="${botColor}"/>` +
    `<path d="M0 ${y} Q80 ${y - 8} 160 ${y - 2} Q240 ${y + 4} 320 ${y - 6} L320 ${VB_H} L0 ${VB_H} Z" fill="${topColor}"/>`
  );
}

export const SCENES = {
  /* 1. 冥王代 — マグマオーシャンと巨大な月 */
  hadean: {
    vbW: VB_W,
    vbH: VB_H,
    bg: "linear-gradient(#2b0a05,#120303)",
    svg:
      `<defs>
         <radialGradient id="moonG" cx="40%" cy="35%" r="70%">
           <stop offset="0" stop-color="#f0d9c0"/><stop offset="1" stop-color="#8a6a55"/>
         </radialGradient>
         <linearGradient id="lavaG" x1="0" y1="0" x2="0" y2="1">
           <stop offset="0" stop-color="#ff8c32"/><stop offset="1" stop-color="#8f1d05"/>
         </linearGradient>
       </defs>` +
      `<circle class="jsun-glow" cx="252" cy="42" r="42" fill="#ffb37a" opacity=".12"/>` +
      `<circle class="jsun" cx="252" cy="42" r="30" fill="url(#moonG)"/>` +
      `<circle cx="244" cy="34" r="5" fill="#00000022"/><circle cx="262" cy="50" r="7" fill="#00000022"/>` +
      // 落下する隕石
      `<g class="jflicker"><path d="M40 12 L58 34" stroke="#ffd7a0" stroke-width="2" stroke-linecap="round"/>` +
      `<circle cx="59" cy="35" r="2.6" fill="#fff0d0"/>` +
      `<path d="M96 4 L108 20" stroke="#ffd7a0" stroke-width="1.4" stroke-linecap="round" opacity=".7"/></g>` +
      // 溶岩の海
      `<rect x="0" y="126" width="320" height="54" fill="url(#lavaG)"/>` +
      `<path d="M0 126 Q40 118 76 126 Q112 134 150 124 Q190 116 228 126 Q266 134 320 122 L320 180 L0 180Z" fill="#5c1403"/>` +
      `<path d="M0 138 Q60 132 120 140 Q180 148 240 138 Q290 130 320 136" stroke="#ff9a3c" stroke-width="2" fill="none" opacity=".8"/>` +
      // 火山
      `<path d="M244 128 L272 92 L300 128 Z" fill="#3a1108"/>` +
      `<path d="M266 96 q6 -10 12 0 q-6 6 -12 0" fill="#ff6a1a" class="jflicker"/>` +
      `<path d="M20 130 L44 104 L68 130 Z" fill="#3a1108"/>`,
    route: "M14 148 Q70 132 122 142 Q176 152 226 134 Q272 118 308 126",
    routeStroke: "#ffb066",
    routeDim: "#ffffff26",
    labelColor: "#ffe9d8",
    labelHalo: "#00000099",
    trail: "bubble",
    stars: 26,
    skyH: 118,
  },

  /* 2. 太古代 — 熱水噴出孔とストロマトライト */
  archean: {
    vbW: VB_W,
    vbH: VB_H,
    bg: "linear-gradient(#04231f,#01100f)",
    svg:
      `<defs><linearGradient id="seaA" x1="0" y1="0" x2="0" y2="1">
         <stop offset="0" stop-color="#0b4a45"/><stop offset="1" stop-color="#02201f"/>
       </linearGradient></defs>` +
      `<rect x="0" y="0" width="320" height="180" fill="url(#seaA)"/>` +
      // 水面の光
      `<path d="M0 16 Q60 8 120 16 Q180 24 240 14 Q290 6 320 14" stroke="#6fe0d0" stroke-width="1.6" fill="none" opacity=".35"/>` +
      ground("#0d3a34", "#082825", 138) +
      // 熱水噴出孔（ブラックスモーカー）
      `<path d="M56 140 L64 108 L74 108 L82 140 Z" fill="#1a1512"/>` +
      `<g class="jbubble"><ellipse cx="69" cy="98" rx="7" ry="10" fill="#8fa3a0" opacity=".45"/></g>` +
      `<g class="jbubble2"><ellipse cx="72" cy="84" rx="10" ry="13" fill="#8fa3a0" opacity=".28"/></g>` +
      `<path d="M232 142 L238 118 L246 118 L252 142 Z" fill="#1a1512"/>` +
      `<g class="jbubble2"><ellipse cx="242" cy="110" rx="6" ry="8" fill="#8fa3a0" opacity=".35"/></g>` +
      // ストロマトライト
      `<g>
         <ellipse cx="150" cy="140" rx="14" ry="6" fill="#2f5c4a"/>
         <ellipse cx="150" cy="132" rx="12" ry="6" fill="#3a6f58"/>
         <ellipse cx="150" cy="124" rx="10" ry="5" fill="#478066"/>
         <ellipse cx="186" cy="142" rx="12" ry="5" fill="#2f5c4a"/>
         <ellipse cx="186" cy="135" rx="10" ry="5" fill="#3a6f58"/>
       </g>` +
      `<g class="jbubble"><circle cx="196" cy="120" r="2.4" fill="#9fe8d8" opacity=".6"/></g>` +
      `<g class="jbubble2"><circle cx="120" cy="112" r="2" fill="#9fe8d8" opacity=".5"/></g>`,
    route: "M12 120 Q64 104 112 118 Q164 132 214 116 Q262 100 310 112",
    routeStroke: "#6fe0d0",
    routeDim: "#ffffff22",
    labelColor: "#dffaf4",
    labelHalo: "#00000099",
    trail: "bubble",
    stars: 0,
  },

  /* 3. 原生代 — 縞状鉄鉱層と凍りついた海 */
  proterozoic: {
    vbW: VB_W,
    vbH: VB_H,
    bg: "linear-gradient(#0a2436,#03121c)",
    svg:
      `<rect x="0" y="0" width="320" height="180" fill="#0a2436"/>` +
      // 氷
      `<path d="M0 40 L44 24 L86 42 L130 22 L176 44 L220 26 L266 44 L320 28 L320 0 L0 0Z" fill="#cfe8f2" opacity=".18"/>` +
      `<path d="M0 46 L44 30 L86 48 L130 28 L176 50 L220 32 L266 50 L320 34" stroke="#bfe4f2" stroke-width="1.6" fill="none" opacity=".5"/>` +
      // 縞状鉄鉱層
      ground("#3a2a26", "#241a18", 132) +
      `<g opacity=".7">
         <path d="M0 142 Q80 138 160 144 Q240 150 320 142" stroke="#8a5a3c" stroke-width="3" fill="none"/>
         <path d="M0 152 Q80 148 160 154 Q240 160 320 152" stroke="#5f7f8a" stroke-width="3" fill="none"/>
         <path d="M0 162 Q80 158 160 164 Q240 170 320 162" stroke="#8a5a3c" stroke-width="3" fill="none"/>
       </g>` +
      // エディアカラ生物群のシルエット
      `<g opacity=".8">
         <path d="M96 132 q4 -22 10 -26 q6 4 10 26" fill="none" stroke="#7fb8a8" stroke-width="2.2"/>
         <path d="M100 124 q6 -4 12 0 M101 116 q5 -3 10 0" stroke="#7fb8a8" stroke-width="1.4" fill="none"/>
         <ellipse cx="214" cy="126" rx="13" ry="7" fill="#5f9c8c" opacity=".7"/>
         <path d="M204 126 q10 -6 20 0" stroke="#8fd0c0" stroke-width="1.2" fill="none"/>
       </g>` +
      `<g class="jbubble"><circle cx="150" cy="104" r="2.2" fill="#9fe8d8" opacity=".5"/></g>` +
      `<g class="jbubble2"><circle cx="252" cy="96" r="2.6" fill="#9fe8d8" opacity=".45"/></g>`,
    route: "M12 112 Q66 98 116 110 Q168 122 218 106 Q264 92 310 104",
    routeStroke: "#8fd8ff",
    routeDim: "#ffffff22",
    labelColor: "#e6f6ff",
    labelHalo: "#00000099",
    trail: "bubble",
    stars: 18,
    skyH: 40,
  },

  /* 4. カンブリア紀 — 浅い海の底 */
  cambrian: {
    vbW: VB_W,
    vbH: VB_H,
    bg: "linear-gradient(#0b3c52,#04202e)",
    svg:
      `<defs><linearGradient id="seaC" x1="0" y1="0" x2="0" y2="1">
         <stop offset="0" stop-color="#128aa8"/><stop offset="1" stop-color="#052736"/>
       </linearGradient></defs>` +
      `<rect x="0" y="0" width="320" height="180" fill="url(#seaC)"/>` +
      // 差し込む光
      `<g opacity=".16">
         <path d="M40 0 L64 0 L34 130 L18 130Z" fill="#bff0ff"/>
         <path d="M150 0 L172 0 L146 130 L130 130Z" fill="#bff0ff"/>
         <path d="M250 0 L268 0 L246 130 L232 130Z" fill="#bff0ff"/>
       </g>` +
      ground("#c9b48a", "#a8926c", 142) +
      // アノマロカリス
      `<g opacity=".9" transform="translate(226,64)">
         <path d="M0 0 q26 -12 52 0 q-26 12 -52 0Z" fill="#e07a5f"/>
         <path d="M6 -2 q-8 -8 -14 -6 M6 2 q-8 8 -14 6" stroke="#e07a5f" stroke-width="2.4" fill="none" stroke-linecap="round"/>
         <path d="M14 6 q8 6 16 4 M30 6 q8 6 16 3" stroke="#c25a3f" stroke-width="1.8" fill="none"/>
         <circle cx="4" cy="-3" r="3" fill="#2a1410"/>
       </g>` +
      // 三葉虫
      `<g transform="translate(88,132)">
         <ellipse cx="0" cy="0" rx="15" ry="8" fill="#6b7f8f"/>
         <path d="M-10 -6 h20 M-12 -1 h24 M-10 4 h20" stroke="#43535f" stroke-width="1.4"/>
       </g>` +
      // 海綿・カイメン
      `<path d="M150 142 q-4 -22 4 -26 q8 4 4 26Z" fill="#d98cae" opacity=".8"/>` +
      `<path d="M166 142 q-3 -16 3 -19 q6 3 3 19Z" fill="#c9739c" opacity=".8"/>` +
      `<g class="jbubble"><circle cx="120" cy="100" r="2.4" fill="#cff4ff" opacity=".5"/></g>` +
      `<g class="jbubble2"><circle cx="200" cy="112" r="2" fill="#cff4ff" opacity=".45"/></g>`,
    route: "M12 128 Q64 112 114 126 Q166 140 216 122 Q264 106 310 120",
    routeStroke: "#ffd166",
    routeDim: "#ffffff26",
    labelColor: "#eefaff",
    labelHalo: "#00000099",
    trail: "bubble",
    stars: 0,
  },

  /* 5. オルドビス紀 — サンゴ礁と上陸しはじめた緑 */
  ordovician: {
    vbW: VB_W,
    vbH: VB_H,
    bg: "linear-gradient(#0e4f66,#062a38)",
    svg:
      `<rect x="0" y="0" width="320" height="180" fill="#0e4f66"/>` +
      `<path d="M0 26 Q60 18 120 26 Q180 34 240 24 Q290 16 320 24" stroke="#9fe4f5" stroke-width="1.6" fill="none" opacity=".35"/>` +
      // 岸辺（右側が陸）
      `<path d="M232 180 L232 118 Q262 108 320 104 L320 180Z" fill="#5f7a4a"/>` +
      `<path d="M232 118 Q262 108 320 104" stroke="#87a866" stroke-width="2" fill="none"/>` +
      // 最初の陸上植物（コケ〜初期維管束植物）
      forkPlant(258, 108, 14) +
      forkPlant(288, 103, 17) +
      forkPlant(308, 100, 11) +
      ground("#c9b48a", "#a08a64", 144) +
      // サンゴ
      `<g>
         <path d="M56 144 q-3 -18 2 -22 q6 4 3 22Z" fill="#f0a0b0"/>
         <path d="M66 144 q-2 -13 2 -16 q5 3 2 16Z" fill="#e8869c"/>
         <path d="M120 144 q-4 -20 3 -24 q7 4 3 24Z" fill="#ffc98a"/>
         <path d="M132 144 q-2 -12 2 -15 q5 3 2 15Z" fill="#f2b070"/>
       </g>` +
      // オウムガイ
      `<g transform="translate(178,80)">
         <path d="M0 0 a13 13 0 1 1 6 -11 l-6 11Z" fill="#e8d5a8" stroke="#b09a6a" stroke-width="1.4"/>
         <path d="M-2 4 q8 6 16 4 M-4 8 q8 7 14 6" stroke="#c9b58a" stroke-width="1.6" fill="none" stroke-linecap="round"/>
         <circle cx="2" cy="2" r="2" fill="#3a2c18"/>
       </g>` +
      `<g class="jbubble"><circle cx="100" cy="106" r="2.2" fill="#cff4ff" opacity=".5"/></g>`,
    route: "M12 132 Q62 118 110 130 Q160 142 208 126 Q256 110 304 112",
    routeStroke: "#ffd166",
    routeDim: "#ffffff26",
    labelColor: "#eefaff",
    labelHalo: "#00000099",
    trail: "bubble",
    stars: 0,
  },

  /* 6. シルル紀 — 水際、最初の陸上生態系 */
  silurian: {
    vbW: VB_W,
    vbH: VB_H,
    bg: "linear-gradient(#3d6d7a,#17414c)",
    svg:
      `<rect x="0" y="0" width="320" height="96" fill="#5f96a4"/>` +
      `<circle class="jsun" cx="272" cy="30" r="17" fill="#ffe9a0"/>` +
      `<circle class="jsun-glow" cx="272" cy="30" r="26" fill="#ffe9a0" opacity=".2"/>` +
      `<ellipse class="jcloud" cx="76" cy="26" rx="30" ry="9" fill="#e8f4f6" opacity=".55"/>` +
      `<ellipse class="jcloud2" cx="172" cy="18" rx="22" ry="7" fill="#e8f4f6" opacity=".4"/>` +
      // 遠景の丘
      `<path d="M0 96 Q60 70 130 92 Q200 112 320 84 L320 96Z" fill="#4a7060"/>` +
      // 水
      `<rect x="0" y="96" width="320" height="84" fill="#2d6470"/>` +
      `<path d="M0 106 Q40 100 80 106 Q120 112 160 106 Q200 100 240 106 Q280 112 320 106" stroke="#7fc0cc" stroke-width="1.4" fill="none" opacity=".5"/>` +
      // 岸
      `<path d="M0 180 L0 128 Q70 120 140 132 Q220 146 320 130 L320 180Z" fill="#6b8a52"/>` +
      // クックソニア（最初の維管束植物）
      forkPlant(40, 128, 20) +
      forkPlant(62, 130, 24) +
      forkPlant(212, 138, 22) +
      forkPlant(244, 136, 17) +
      // ウミサソリ
      `<g transform="translate(140,152)">
         <ellipse cx="0" cy="0" rx="13" ry="6" fill="#6a4a3a"/>
         <path d="M12 0 q8 -3 12 -8 M12 2 q9 2 13 7" stroke="#6a4a3a" stroke-width="2" fill="none" stroke-linecap="round"/>
         <path d="M-13 0 q-9 2 -12 8" stroke="#6a4a3a" stroke-width="2" fill="none" stroke-linecap="round"/>
       </g>`,
    route: "M12 150 Q62 138 110 146 Q160 154 208 140 Q256 126 306 132",
    routeStroke: "#ffd166",
    routeDim: "#ffffff2e",
    labelColor: "#f2fbff",
    labelHalo: "#00000099",
    trail: "foot",
    stars: 0,
  },

  /* 7. デボン紀 — 最初の森と、泥から上がる四肢動物 */
  devonian: {
    vbW: VB_W,
    vbH: VB_H,
    bg: "linear-gradient(#7a9a6a,#2f4a38)",
    svg:
      `<rect x="0" y="0" width="320" height="104" fill="#9db98a"/>` +
      `<circle class="jsun" cx="46" cy="28" r="15" fill="#fff0b0"/>` +
      `<ellipse class="jcloud" cx="180" cy="24" rx="34" ry="10" fill="#f0f6ea" opacity=".5"/>` +
      // アーケオプテリスの森
      `<g>
         <path d="M228 104 v-46" stroke="#5a4530" stroke-width="6"/>
         <path d="M228 60 q-26 -6 -34 8 q22 -2 34 4 M228 60 q26 -6 34 8 q-22 -2 -34 4 M228 48 q-20 -8 -26 2 q18 0 26 6 M228 48 q20 -8 26 2 q-18 0 -26 6" fill="#4f7a48"/>
         <path d="M282 104 v-36" stroke="#5a4530" stroke-width="5"/>
         <path d="M282 70 q-20 -6 -26 6 q18 -2 26 2 M282 70 q20 -6 26 6 q-18 -2 -26 2" fill="#456e40"/>
         <path d="M160 104 v-28" stroke="#5a4530" stroke-width="4"/>
         <path d="M160 78 q-16 -5 -20 5 q14 -2 20 2 M160 78 q16 -5 20 5 q-14 -2 -20 2" fill="#456e40"/>
       </g>` +
      ground("#6f8a5a", "#4a5f3e", 104) +
      // 水たまり
      `<ellipse cx="96" cy="150" rx="72" ry="22" fill="#3f6a70" opacity=".9"/>` +
      `<ellipse cx="96" cy="148" rx="60" ry="16" fill="#57858c" opacity=".6"/>` +
      `<path d="M56 146 q18 -4 36 0 M74 154 q20 -4 38 1" stroke="#8fc0c4" stroke-width="1.2" fill="none" opacity=".6"/>` +
      // 岸に上がる四肢動物のシルエット
      `<g transform="translate(196,140)" opacity=".85">
         <ellipse cx="0" cy="0" rx="16" ry="6" fill="#4a5a3a"/>
         <ellipse cx="17" cy="-3" rx="7" ry="5" fill="#4a5a3a"/>
         <path d="M-10 5 l-3 7 M-2 6 l-1 7 M8 6 l2 7 M15 4 l4 7" stroke="#4a5a3a" stroke-width="2.4" stroke-linecap="round"/>
         <path d="M-16 0 q-10 -2 -14 4" stroke="#4a5a3a" stroke-width="3" fill="none" stroke-linecap="round"/>
       </g>` +
      // シダ
      `<g stroke="#5f8a4a" stroke-width="1.8" fill="none" stroke-linecap="round">
         <path d="M22 168 q4 -20 -4 -28 M22 160 q10 -4 14 -12 M22 152 q-10 -4 -14 -12"/>
       </g>`,
    route: "M10 166 Q60 156 108 162 Q160 168 208 152 Q258 136 310 142",
    routeStroke: "#ffb84d",
    routeDim: "#ffffff33",
    labelColor: "#f4fbee",
    labelHalo: "#00000099",
    trail: "foot",
    stars: 0,
  },

  /* 8. 石炭紀 — 巨大シダの森と巨大トンボ */
  carboniferous: {
    vbW: VB_W,
    vbH: VB_H,
    bg: "linear-gradient(#2b4a2a,#0f2415)",
    svg:
      `<rect x="0" y="0" width="320" height="180" fill="#2f5230"/>` +
      // 霧の層
      `<rect x="0" y="0" width="320" height="60" fill="#8fbf8a" opacity=".18"/>` +
      // リンボク（鱗木）
      `<g>
         <path d="M40 150 v-118" stroke="#4a3b26" stroke-width="9"/>
         <path d="M36 132 h8 M36 118 h8 M36 104 h8 M36 90 h8 M36 76 h8 M36 62 h8" stroke="#3a2e1e" stroke-width="1.6"/>
         <path d="M40 34 q-24 -12 -32 2 q20 0 32 6 M40 34 q24 -12 32 2 q-20 0 -32 6 M40 24 q-16 -12 -22 -2 q14 2 22 8 M40 24 q16 -12 22 -2 q-14 2 -22 8" fill="#3f7a42"/>
         <path d="M124 154 v-96" stroke="#4a3b26" stroke-width="7"/>
         <path d="M120 138 h8 M120 122 h8 M120 106 h8 M120 90 h8" stroke="#3a2e1e" stroke-width="1.4"/>
         <path d="M124 58 q-20 -10 -27 2 q17 0 27 5 M124 58 q20 -10 27 2 q-17 0 -27 5" fill="#3f7a42"/>
         <path d="M252 152 v-108" stroke="#4a3b26" stroke-width="8"/>
         <path d="M248 134 h8 M248 118 h8 M248 100 h8 M248 82 h8" stroke="#3a2e1e" stroke-width="1.5"/>
         <path d="M252 44 q-22 -12 -30 0 q19 0 30 6 M252 44 q22 -12 30 0 q-19 0 -30 6" fill="#3f7a42"/>
         <path d="M300 158 v-72" stroke="#4a3b26" stroke-width="6"/>
         <path d="M300 86 q-17 -9 -23 1 q14 0 23 4 M300 86 q17 -9 23 1 q-14 0 -23 4" fill="#376b3a"/>
       </g>` +
      ground("#3f5c34", "#25381f", 150) +
      // メガネウラ（巨大トンボ）
      `<g class="jcloud" transform="translate(186,58)">
         <path d="M0 0 h26" stroke="#6a7a3a" stroke-width="3" stroke-linecap="round"/>
         <ellipse cx="4" cy="-6" rx="14" ry="3.4" fill="#a8d0e0" opacity=".75" transform="rotate(-12 4 -6)"/>
         <ellipse cx="4" cy="6" rx="14" ry="3.4" fill="#a8d0e0" opacity=".75" transform="rotate(12 4 6)"/>
         <circle cx="-2" cy="0" r="3.4" fill="#3a4a1a"/>
       </g>` +
      // 倒木と石炭になる材
      `<path d="M60 158 q30 -6 62 2" stroke="#3a2e1e" stroke-width="6" stroke-linecap="round" fill="none"/>` +
      `<g stroke="#4f8a4a" stroke-width="1.6" fill="none" stroke-linecap="round">
         <path d="M164 168 q4 -16 -4 -22 M164 160 q10 -3 13 -10"/>
         <path d="M216 172 q4 -14 -3 -20"/>
       </g>`,
    route: "M10 168 Q62 158 110 166 Q162 174 210 160 Q260 146 312 152",
    routeStroke: "#ffd166",
    routeDim: "#ffffff33",
    labelColor: "#eefae8",
    labelHalo: "#00000099",
    trail: "foot",
    stars: 0,
  },

  /* 9. ペルム紀 — 乾いたパンゲアと噴火の空 */
  permian: {
    vbW: VB_W,
    vbH: VB_H,
    bg: "linear-gradient(#8a5a3a,#3a2016)",
    svg:
      `<defs><linearGradient id="skyP" x1="0" y1="0" x2="0" y2="1">
         <stop offset="0" stop-color="#c97a4a"/><stop offset="1" stop-color="#e8b070"/>
       </linearGradient></defs>` +
      `<rect x="0" y="0" width="320" height="120" fill="url(#skyP)"/>` +
      `<circle class="jsun" cx="248" cy="34" r="16" fill="#ffdca0" opacity=".9"/>` +
      // 噴煙
      `<g opacity=".55">
         <ellipse class="jcloud" cx="60" cy="30" rx="34" ry="14" fill="#6a5a55"/>
         <ellipse class="jcloud2" cx="110" cy="20" rx="26" ry="10" fill="#6a5a55"/>
       </g>` +
      // 遠景の火山
      `<path d="M20 120 L58 52 L96 120 Z" fill="#5a3a2a"/>` +
      `<path d="M50 60 q8 -8 16 0 q-8 6 -16 0" fill="#ff7a2a" class="jflicker"/>` +
      `<path d="M100 120 L128 78 L156 120 Z" fill="#6a4632" opacity=".8"/>` +
      // 乾いた大地
      ground("#c99a6a", "#a87a52", 120) +
      `<path d="M0 140 q60 -6 120 2 q60 8 120 -2 q40 -6 80 0" stroke="#a87a52" stroke-width="1.6" fill="none" opacity=".7"/>` +
      // ひび割れ
      `<g stroke="#8a6242" stroke-width="1.2" fill="none" opacity=".7">
         <path d="M40 156 l14 8 l-6 10 M56 164 l16 -4"/>
         <path d="M200 150 l12 10 l-4 10 M212 160 l14 -2"/>
       </g>` +
      // ディメトロドン（単弓類）のシルエット
      `<g transform="translate(230,138)" opacity=".9">
         <path d="M-14 -6 Q0 -34 16 -6 Z" fill="#6a4a3a"/>
         <ellipse cx="0" cy="0" rx="20" ry="7" fill="#5a3f30"/>
         <ellipse cx="22" cy="-3" rx="8" ry="5" fill="#5a3f30"/>
         <path d="M-12 6 l-3 9 M-2 7 l-1 9 M8 7 l2 9 M18 5 l4 9" stroke="#5a3f30" stroke-width="2.6" stroke-linecap="round"/>
         <path d="M-20 0 q-12 -2 -16 6" stroke="#5a3f30" stroke-width="3" fill="none" stroke-linecap="round"/>
       </g>` +
      // 枯れた低木
      `<path d="M84 156 v-14 M78 148 l6 -6 M90 148 l-6 -6" stroke="#7a5a3a" stroke-width="1.8" fill="none" stroke-linecap="round"/>`,
    route: "M10 158 Q60 148 108 156 Q158 164 206 150 Q256 136 310 144",
    routeStroke: "#ffb84d",
    routeDim: "#ffffff33",
    labelColor: "#fff0e0",
    labelHalo: "#00000099",
    trail: "dust",
    stars: 0,
  },

  /* 10. 三畳紀 — 回復する世界と最初の恐竜 */
  triassic: {
    vbW: VB_W,
    vbH: VB_H,
    bg: "linear-gradient(#b08a5a,#4a3a26)",
    svg:
      `<rect x="0" y="0" width="320" height="112" fill="#d8a86a"/>` +
      `<circle class="jsun" cx="60" cy="30" r="17" fill="#fff0c0"/>` +
      `<circle class="jsun-glow" cx="60" cy="30" r="27" fill="#fff0c0" opacity=".2"/>` +
      `<ellipse class="jcloud" cx="196" cy="24" rx="32" ry="9" fill="#f4e8d0" opacity=".55"/>` +
      // 遠くの台地
      `<path d="M0 112 L0 84 L52 84 L52 112Z M232 112 L232 72 L292 72 L292 112Z" fill="#a87a4a" opacity=".7"/>` +
      ground("#b8905c", "#8a6a42", 112) +
      // ソテツ
      `<g>
         <path d="M100 148 v-22" stroke="#5a4530" stroke-width="5"/>
         <path d="M100 126 q-20 -4 -24 6 q16 -2 24 2 M100 126 q20 -4 24 6 q-16 -2 -24 2 M100 120 q-14 -10 -18 -2 q12 0 18 6 M100 120 q14 -10 18 -2 q-12 0 -18 6" fill="#3f7a48"/>
         <path d="M276 150 v-18" stroke="#5a4530" stroke-width="4"/>
         <path d="M276 132 q-16 -3 -19 5 q13 -2 19 2 M276 132 q16 -3 19 5 q-13 -2 -19 2" fill="#3f7a48"/>
       </g>` +
      // 初期の恐竜（コエロフィシス）
      `<g transform="translate(196,140)" opacity=".92">
         <ellipse cx="0" cy="0" rx="16" ry="6" fill="#7a5a3a"/>
         <path d="M-16 0 q-16 4 -22 -4" stroke="#7a5a3a" stroke-width="3.4" fill="none" stroke-linecap="round"/>
         <path d="M14 -2 q10 -12 14 -22" stroke="#7a5a3a" stroke-width="4" fill="none" stroke-linecap="round"/>
         <ellipse cx="29" cy="-25" rx="7" ry="4" fill="#7a5a3a"/>
         <path d="M-2 6 l-4 12 M8 5 l3 13" stroke="#7a5a3a" stroke-width="3" stroke-linecap="round"/>
       </g>` +
      // 小さな哺乳形類
      `<g transform="translate(52,158)" opacity=".85">
         <ellipse cx="0" cy="0" rx="7" ry="4" fill="#5a4a3a"/>
         <circle cx="7" cy="-2" r="3.4" fill="#5a4a3a"/>
         <path d="M-7 0 q-6 -1 -8 3" stroke="#5a4a3a" stroke-width="1.6" fill="none" stroke-linecap="round"/>
       </g>`,
    route: "M10 162 Q60 152 110 160 Q160 168 210 154 Q260 140 312 148",
    routeStroke: "#ffd166",
    routeDim: "#ffffff33",
    labelColor: "#fff6e6",
    labelHalo: "#00000099",
    trail: "foot",
    stars: 0,
  },

  /* 11. ジュラ紀 — 竜脚類のいるシダの平原 */
  jurassic: {
    vbW: VB_W,
    vbH: VB_H,
    bg: "linear-gradient(#6a9a7a,#243a2a)",
    svg:
      `<rect x="0" y="0" width="320" height="110" fill="#8fc0d8"/>` +
      `<circle class="jsun" cx="270" cy="26" r="15" fill="#fff6c0"/>` +
      `<ellipse class="jcloud" cx="80" cy="22" rx="36" ry="11" fill="#ffffff" opacity=".65"/>` +
      `<ellipse class="jcloud2" cx="170" cy="34" rx="26" ry="8" fill="#ffffff" opacity=".5"/>` +
      // 遠景の山
      `<path d="M0 110 L48 56 L96 110Z" fill="#5f7f8a" opacity=".65"/>` +
      `<path d="M60 110 L110 44 L162 110Z" fill="#4f6f7a" opacity=".7"/>` +
      ground("#6f9a5a", "#3f5f3a", 110) +
      // 竜脚類
      `<g transform="translate(180,120)" opacity=".92">
         <ellipse cx="0" cy="0" rx="34" ry="18" fill="#6a7f5a"/>
         <path d="M-34 -2 q-26 6 -40 -8" stroke="#6a7f5a" stroke-width="6" fill="none" stroke-linecap="round"/>
         <path d="M28 -10 q22 -20 26 -48" stroke="#6a7f5a" stroke-width="9" fill="none" stroke-linecap="round"/>
         <ellipse cx="56" cy="-60" rx="9" ry="6" fill="#6a7f5a"/>
         <circle cx="60" cy="-62" r="1.8" fill="#20301c"/>
         <path d="M-16 16 l-3 20 M-2 18 l0 20 M14 16 l4 20 M26 12 l7 20" stroke="#5a6f4a" stroke-width="6" stroke-linecap="round"/>
       </g>` +
      // シダ
      `<g stroke="#4f8a4a" stroke-width="1.8" fill="none" stroke-linecap="round">
         <path d="M30 168 q5 -20 -4 -28 M30 158 q12 -4 15 -12 M30 150 q-12 -4 -15 -12"/>
         <path d="M290 172 q5 -18 -3 -25 M290 162 q11 -4 14 -11"/>
         <path d="M108 176 q4 -14 -3 -20"/>
       </g>` +
      // 始祖鳥
      `<g class="jcloud2" transform="translate(96,50)">
         <path d="M0 0 q-10 -6 -18 -2 q8 2 12 6 M0 0 q10 -6 18 -2 q-8 2 -12 6" fill="#c98a5a"/>
         <ellipse cx="0" cy="2" rx="5" ry="2.4" fill="#a86a3a"/>
       </g>`,
    route: "M10 152 Q62 144 112 152 Q164 160 214 146 Q262 132 312 140",
    routeStroke: "#ffd166",
    routeDim: "#ffffff33",
    labelColor: "#f4fbf0",
    labelHalo: "#00000099",
    trail: "foot",
    stars: 0,
  },

  /* 12. 白亜紀 — 花の咲く世界と落ちてくる小惑星 */
  cretaceous: {
    vbW: VB_W,
    vbH: VB_H,
    bg: "linear-gradient(#4a6a8a,#20301f)",
    svg:
      `<defs><linearGradient id="skyK" x1="0" y1="0" x2="0" y2="1">
         <stop offset="0" stop-color="#2f4a6a"/><stop offset="1" stop-color="#d88a5a"/>
       </linearGradient></defs>` +
      `<rect x="0" y="0" width="320" height="108" fill="url(#skyK)"/>` +
      // 小惑星
      `<g class="jflicker">
         <path d="M228 4 L268 44" stroke="#ffe0a0" stroke-width="3" stroke-linecap="round"/>
         <path d="M232 2 L272 42" stroke="#fff6d0" stroke-width="1.2" stroke-linecap="round"/>
         <circle cx="270" cy="46" r="5" fill="#fff0c0"/>
         <circle cx="270" cy="46" r="9" fill="#ffb060" opacity=".4"/>
       </g>` +
      `<ellipse class="jcloud" cx="70" cy="26" rx="32" ry="9" fill="#e8d0c0" opacity=".45"/>` +
      // 森
      `<g>
         <path d="M26 108 v-30" stroke="#4a3b26" stroke-width="5"/>
         <ellipse cx="26" cy="74" rx="20" ry="14" fill="#3f6b3a"/>
         <path d="M300 108 v-26" stroke="#4a3b26" stroke-width="5"/>
         <ellipse cx="300" cy="80" rx="18" ry="12" fill="#3f6b3a"/>
       </g>` +
      ground("#4f7040", "#2c4429", 108) +
      // ティラノサウルス
      `<g transform="translate(160,126)" opacity=".92">
         <path d="M-20 -4 q-24 2 -34 -12" stroke="#6a5a3a" stroke-width="7" fill="none" stroke-linecap="round"/>
         <ellipse cx="0" cy="0" rx="22" ry="12" fill="#6a5a3a"/>
         <path d="M16 -8 q12 -14 14 -26" stroke="#6a5a3a" stroke-width="8" fill="none" stroke-linecap="round"/>
         <path d="M30 -36 q14 -2 16 6 q-12 6 -18 2Z" fill="#6a5a3a"/>
         <path d="M32 -32 l10 0 M34 -28 l8 1" stroke="#f0e8d0" stroke-width="1" />
         <circle cx="34" cy="-36" r="1.8" fill="#20180c"/>
         <path d="M10 4 q8 4 8 10" stroke="#6a5a3a" stroke-width="2.6" fill="none" stroke-linecap="round"/>
         <path d="M-6 12 l-4 22 M10 10 l6 24" stroke="#5f4f32" stroke-width="7" stroke-linecap="round"/>
       </g>` +
      // 被子植物（花）
      `<g>
         <path d="M60 168 v-14 M240 172 v-12 M280 166 v-10" stroke="#4f8a4a" stroke-width="1.6" stroke-linecap="round"/>
         <circle cx="60" cy="152" r="3.4" fill="#f0a0c0"/><circle cx="60" cy="152" r="1.4" fill="#ffe08a"/>
         <circle cx="240" cy="158" r="3" fill="#ffd0e0"/><circle cx="240" cy="158" r="1.2" fill="#ffe08a"/>
         <circle cx="280" cy="154" r="2.8" fill="#e8a0d0"/>
       </g>`,
    route: "M10 158 Q60 150 110 158 Q162 166 212 152 Q260 138 312 146",
    routeStroke: "#ffb0d0",
    routeDim: "#ffffff33",
    labelColor: "#fff4ec",
    labelHalo: "#00000099",
    trail: "foot",
    stars: 14,
    skyH: 100,
  },

  /* 13. 古第三紀 — 温暖な森と多様化する哺乳類 */
  paleogene: {
    vbW: VB_W,
    vbH: VB_H,
    bg: "linear-gradient(#8ab08a,#2f4a34)",
    svg:
      `<rect x="0" y="0" width="320" height="104" fill="#a8d0e8"/>` +
      `<circle class="jsun" cx="52" cy="26" r="16" fill="#fff8c8"/>` +
      `<circle class="jsun-glow" cx="52" cy="26" r="26" fill="#fff8c8" opacity=".22"/>` +
      `<ellipse class="jcloud" cx="180" cy="20" rx="38" ry="11" fill="#ffffff" opacity=".7"/>` +
      `<ellipse class="jcloud2" cx="264" cy="34" rx="26" ry="8" fill="#ffffff" opacity=".55"/>` +
      // 広葉樹の森
      `<g>
         <path d="M40 104 v-28" stroke="#5a4530" stroke-width="6"/>
         <ellipse cx="40" cy="70" rx="24" ry="18" fill="#3f7a48"/>
         <ellipse cx="30" cy="62" rx="14" ry="11" fill="#4a8a52"/>
         <path d="M118 104 v-22" stroke="#5a4530" stroke-width="5"/>
         <ellipse cx="118" cy="78" rx="19" ry="14" fill="#458046"/>
         <path d="M266 104 v-32" stroke="#5a4530" stroke-width="6"/>
         <ellipse cx="266" cy="66" rx="26" ry="19" fill="#3f7a48"/>
         <ellipse cx="278" cy="58" rx="14" ry="10" fill="#4a8a52"/>
       </g>` +
      ground("#5f8a4a", "#3a5a34", 104) +
      // 初期の哺乳類
      `<g transform="translate(180,142)" opacity=".9">
         <ellipse cx="0" cy="0" rx="15" ry="8" fill="#8a6a4a"/>
         <circle cx="15" cy="-5" r="7" fill="#8a6a4a"/>
         <path d="M11 -11 l-2 -6 l6 3Z M19 -11 l3 -6 l-6 3Z" fill="#8a6a4a"/>
         <path d="M-15 -2 q-11 -4 -13 4" stroke="#8a6a4a" stroke-width="2.6" fill="none" stroke-linecap="round"/>
         <path d="M-8 7 l-2 10 M2 8 l1 10 M10 6 l3 10" stroke="#7a5a3a" stroke-width="2.8" stroke-linecap="round"/>
         <circle cx="18" cy="-6" r="1.6" fill="#20180c"/>
       </g>` +
      // 下草
      `<g stroke="#4f8a4a" stroke-width="1.6" fill="none" stroke-linecap="round">
         <path d="M76 168 q4 -14 -3 -20 M76 160 q10 -3 12 -9"/>
         <path d="M300 172 q4 -12 -3 -17"/>
       </g>`,
    route: "M10 160 Q60 152 110 160 Q160 168 210 154 Q258 140 310 148",
    routeStroke: "#ffd166",
    routeDim: "#ffffff33",
    labelColor: "#f4fbf0",
    labelHalo: "#00000099",
    trail: "foot",
    stars: 0,
  },

  /* 14. 新第三紀 — 草原の広がりと類人猿 */
  neogene: {
    vbW: VB_W,
    vbH: VB_H,
    bg: "linear-gradient(#c9b070,#5a4a2a)",
    svg:
      `<rect x="0" y="0" width="320" height="106" fill="#e8c88a"/>` +
      `<circle class="jsun" cx="264" cy="28" r="18" fill="#fff0a0"/>` +
      `<circle class="jsun-glow" cx="264" cy="28" r="30" fill="#ffdc70" opacity=".2"/>` +
      `<ellipse class="jcloud" cx="90" cy="24" rx="34" ry="8" fill="#fff6e0" opacity=".6"/>` +
      // 遠景のサバンナ
      `<path d="M0 106 Q80 96 160 104 Q240 112 320 100 L320 106Z" fill="#b09a5a"/>` +
      ground("#c9a860", "#9a8244", 106) +
      // アカシア
      `<g>
         <path d="M56 150 v-32" stroke="#6a5230" stroke-width="4"/>
         <path d="M28 118 q28 -16 56 0 q-28 8 -56 0Z" fill="#5f7a3a"/>
         <path d="M262 154 v-26" stroke="#6a5230" stroke-width="3.4"/>
         <path d="M240 128 q22 -13 44 0 q-22 7 -44 0Z" fill="#5f7a3a"/>
       </g>` +
      // 草
      `<g stroke="#a89050" stroke-width="1.4" fill="none" stroke-linecap="round" opacity=".9">
         <path d="M14 172 q2 -10 -2 -14 M22 174 q3 -11 -1 -15 M30 172 q2 -9 -2 -13"/>
         <path d="M148 176 q2 -10 -2 -14 M156 178 q3 -11 -1 -15"/>
         <path d="M300 174 q2 -10 -2 -14 M308 176 q3 -11 -1 -15"/>
       </g>` +
      // 二足で立つ類人猿
      `<g transform="translate(178,138)" opacity=".92">
         <circle cx="0" cy="-26" r="7" fill="#6a4a32"/>
         <path d="M0 -19 v16" stroke="#6a4a32" stroke-width="9" stroke-linecap="round"/>
         <path d="M-4 -14 l-9 10 M4 -14 l9 10" stroke="#6a4a32" stroke-width="3.4" stroke-linecap="round"/>
         <path d="M-3 -3 l-3 16 M3 -3 l4 16" stroke="#6a4a32" stroke-width="4" stroke-linecap="round"/>
       </g>` +
      // 群れの遠景
      `<g opacity=".45" fill="#7a5a3a">
         <ellipse cx="100" cy="120" rx="7" ry="3.4"/><ellipse cx="114" cy="122" rx="6" ry="3"/>
       </g>`,
    route: "M10 164 Q60 156 110 164 Q160 172 210 158 Q258 144 310 152",
    routeStroke: "#ffb84d",
    routeDim: "#ffffff33",
    labelColor: "#fff8e8",
    labelHalo: "#00000099",
    trail: "foot",
    stars: 0,
  },

  /* 15. 第四紀 — 氷期の大地とマンモス、そして火 */
  quaternary: {
    vbW: VB_W,
    vbH: VB_H,
    bg: "linear-gradient(#3a5a7a,#16242f)",
    svg:
      `<rect x="0" y="0" width="320" height="110" fill="#7fa8c8"/>` +
      `<circle class="jsun" cx="58" cy="28" r="14" fill="#fff4d8" opacity=".85"/>` +
      `<ellipse class="jcloud" cx="200" cy="22" rx="40" ry="10" fill="#e8f0f6" opacity=".6"/>` +
      // 氷河・雪山
      `<path d="M0 110 L46 44 L92 110Z" fill="#c8dce8"/>` +
      `<path d="M46 44 L60 62 L46 70 L34 60Z" fill="#ffffff" opacity=".8"/>` +
      `<path d="M180 110 L236 36 L292 110Z" fill="#b8d0e0"/>` +
      `<path d="M236 36 L252 58 L236 66 L222 56Z" fill="#ffffff" opacity=".85"/>` +
      ground("#d8e4ec", "#a8bccc", 110) +
      // 雪原の起伏
      `<path d="M0 132 q60 -8 120 0 q60 8 120 0 q40 -6 80 2" stroke="#ffffff" stroke-width="1.6" fill="none" opacity=".5"/>` +
      // マンモス
      `<g transform="translate(212,140)" opacity=".92">
         <ellipse cx="0" cy="0" rx="24" ry="16" fill="#7a5a42"/>
         <path d="M-24 -6 q-6 -10 2 -12 M-20 -12 q10 -6 18 -2" stroke="#7a5a42" stroke-width="3" fill="none"/>
         <circle cx="-24" cy="-4" r="11" fill="#8a6a4a"/>
         <path d="M-32 4 q-8 8 -4 16" stroke="#8a6a4a" stroke-width="4" fill="none" stroke-linecap="round"/>
         <path d="M-32 0 q-14 6 -14 16" stroke="#f0e8d8" stroke-width="2.6" fill="none" stroke-linecap="round"/>
         <path d="M-26 2 q-12 8 -10 18" stroke="#f0e8d8" stroke-width="2.4" fill="none" stroke-linecap="round"/>
         <path d="M-12 15 l-2 12 M2 16 l1 12 M16 14 l3 12" stroke="#6a4a32" stroke-width="5" stroke-linecap="round"/>
       </g>` +
      // 焚き火とヒト
      `<g transform="translate(76,150)">
         <path d="M0 0 q-5 -10 0 -14 q5 4 4 9 q3 -4 4 -1 q1 6 -8 6Z" fill="#ff8a2a" class="jflicker"/>
         <path d="M-8 2 l16 0" stroke="#5a4530" stroke-width="2.4" stroke-linecap="round"/>
         <g transform="translate(24,-4)">
           <circle cx="0" cy="-12" r="3.4" fill="#3a2a20"/>
           <path d="M0 -9 v8" stroke="#3a2a20" stroke-width="4" stroke-linecap="round"/>
           <path d="M0 -1 l-3 8 M0 -1 l3 8" stroke="#3a2a20" stroke-width="2" stroke-linecap="round"/>
         </g>
       </g>`,
    route: "M10 164 Q60 156 110 162 Q160 168 208 156 Q258 142 310 150",
    routeStroke: "#7bd8ff",
    routeDim: "#ffffff3a",
    labelColor: "#f0f8ff",
    labelHalo: "#00000099",
    trail: "foot",
    stars: 0,
  },

  /* 16. 未来 — 設計される生命 */
  future: {
    vbW: VB_W,
    vbH: VB_H,
    bg: "linear-gradient(#0a1428,#04060f)",
    svg:
      `<rect x="0" y="0" width="320" height="180" fill="#060a18"/>` +
      // オーロラ状の光
      `<g opacity=".28">
         <path class="jcloud" d="M0 40 Q80 16 160 38 Q240 60 320 32 L320 62 Q240 88 160 66 Q80 44 0 68Z" fill="#4fd1a5"/>
         <path class="jcloud2" d="M0 66 Q80 46 160 66 Q240 86 320 60 L320 80 Q240 104 160 84 Q80 64 0 88Z" fill="#7bd8ff"/>
       </g>` +
      // 二重らせん
      `<g class="jflicker" opacity=".8">
         <path d="M40 176 C70 150 70 120 40 96 C10 72 10 42 40 16" stroke="#4fd1a5" stroke-width="2" fill="none"/>
         <path d="M40 176 C10 150 10 120 40 96 C70 72 70 42 40 16" stroke="#7bd8ff" stroke-width="2" fill="none"/>
         <path d="M40 160 h0 M26 140 h28 M26 112 h28 M26 84 h28 M26 56 h28 M26 32 h28" stroke="#ffffff" stroke-width="1.2" opacity=".45"/>
       </g>` +
      // 地平線の都市
      `<path d="M0 160 L320 160 L320 180 L0 180Z" fill="#0c1a26"/>` +
      `<g fill="#12303f">
         <rect x="150" y="132" width="12" height="28"/><rect x="166" y="118" width="10" height="42"/>
         <rect x="180" y="140" width="14" height="20"/><rect x="198" y="124" width="9" height="36"/>
         <rect x="212" y="146" width="12" height="14"/>
       </g>` +
      `<g fill="#7bd8ff" opacity=".8" class="jflicker">
         <rect x="153" y="138" width="2" height="2"/><rect x="169" y="126" width="2" height="2"/>
         <rect x="184" y="146" width="2" height="2"/><rect x="201" y="132" width="2" height="2"/>
       </g>` +
      // 人工細胞
      `<g opacity=".7">
         <circle cx="264" cy="60" r="16" fill="none" stroke="#4fd1a5" stroke-width="1.4" stroke-dasharray="3 4"/>
         <circle cx="264" cy="60" r="6" fill="#4fd1a5" opacity=".35"/>
       </g>`,
    route: "M12 150 Q66 134 116 146 Q168 158 218 140 Q266 124 310 134",
    routeStroke: "#4fd1a5",
    routeDim: "#ffffff2e",
    labelColor: "#e6fbff",
    labelHalo: "#00000099",
    trail: "dust",
    stars: 60,
    skyH: 150,
  },
};

export function sceneFor(eraId) {
  return SCENES[eraId] || SCENES.hadean;
}
